import React, { useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Link as LinkIcon,
  FileText,
  AlertCircle,
  Clock,
  Undo2,
  Trash2,
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Checkbox } from '../../molecules/Checkbox';
import { TagChip, type TagColor } from '../../atoms/TagChip';
import { TagPanel, type TagDefinition } from '../../molecules/TagPanel';
import { PagePreview } from '../../atoms/PagePreview';
import './Document.css';

/**
 * Get icon component by name from lucide-react.
 * Falls back to FileText if icon not found.
 */
function getIconByName(iconName: string | undefined): React.ReactNode {
  if (!iconName) {
    return <FileText className="document-icon-svg" />;
  }

  // Get icon component from lucide-react
  // Lucide icons are ForwardRef components (objects with $$typeof), not plain functions
  const IconComponent = (LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[iconName];

  if (IconComponent) {
    const Icon = IconComponent;
    return <Icon className="document-icon-svg" />;
  }

  // Fallback to FileText
  return <FileText className="document-icon-svg" />;
}

export interface LinkedObject {
  id: number;
  name: string;
}

/** Highlight bubble for extracted facts (folded view) */
export interface HighlightBubble {
  name: string;
  label: string;
  value: string | number;
  style: 'default' | 'alert' | 'success' | 'warning';
  /** Display type: 'text' for label: value, 'scale' for visual bar indicator, 'composite' for label value (space, no colon), 'value-only' for just the value */
  displayType?: 'text' | 'scale' | 'composite' | 'value-only';
  /** Scale type identifier (e.g., 'gehaltsklasse' for German nutrient classes A-E) */
  scaleType?: string;
  /** Value format: 'currency' for € prefix, 'sender_name' to extract name from email, 'datetime_de' for German datetime, 'date_de' for German date (DD.MM.YYYY) */
  format?: 'currency' | 'sender_name' | 'datetime_de' | 'date_de';
}

/** German nutrient classes (Gehaltsklassen) scale mapping */
const GEHALTSKLASSE_SCALE: Record<string, { filled: number; total: number }> = {
  'A': { filled: 1, total: 5 },
  'B': { filled: 2, total: 5 },
  'C': { filled: 3, total: 5 },
  'D': { filled: 4, total: 5 },
  'E': { filled: 5, total: 5 },
};

/** Render a scale value as filled/empty bars */
function renderScaleValue(value: string | number, scaleType?: string): React.ReactNode {
  const strValue = String(value).toUpperCase();
  if (scaleType === 'gehaltsklasse') {
    const scale = GEHALTSKLASSE_SCALE[strValue];
    if (scale) {
      return (
        <span className="document-bubble-scale">
          {'▮'.repeat(scale.filled)}
          {'▯'.repeat(scale.total - scale.filled)}
        </span>
      );
    }
  }
  // Fallback to raw value if scale not found
  return value;
}

/** Format a value based on format type */
function formatValue(value: string | number, format?: 'currency' | 'sender_name' | 'datetime_de' | 'date_de'): string {
  if (format === 'currency') {
    // Parse the number and format with € prefix
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (!isNaN(num)) {
      // Format with German locale (1.234,56 €)
      return `€${num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  }
  if (format === 'sender_name') {
    // Extract name from email format: "Antonio Subkhangulov <a.subkhangulov@gmail.com>" -> "Antonio Subkhangulov"
    const str = String(value);
    // Find the position of '<' and extract everything before it
    const bracketIndex = str.indexOf('<');
    if (bracketIndex > 0) {
      return str.substring(0, bracketIndex).trim();
    }
    // If no angle brackets, check if it's just an email address
    if (str.includes('@') && !str.includes(' ')) {
      // Return the part before @ as fallback
      return str.split('@')[0];
    }
    return str;
  }
  if (format === 'date_de') {
    // Convert ISO date to German format: "2026-03-21" -> "21.03.2026"
    const str = String(value);
    try {
      const date = new Date(str + (str.includes('T') ? '' : 'T00:00:00'));
      if (!isNaN(date.getTime())) {
        const day = date.getUTCDate().toString().padStart(2, '0');
        const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
        const year = date.getUTCFullYear();
        return `${day}.${month}.${year}`;
      }
    } catch {
      // Fall through to return original value
    }
    return str;
  }
  if (format === 'datetime_de') {
    // Convert ISO datetime to German format: "2026-01-20T14:06:08+01:00" -> "20.01.2026 14:06"
    const str = String(value);
    try {
      const date = new Date(str);
      if (!isNaN(date.getTime())) {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day}.${month}.${year} ${hours}:${minutes}`;
      }
    } catch {
      // Fall through to return original value
    }
    return str;
  }
  return String(value);
}

export interface DocumentProps {
  /** Document type (e.g., "Invoice", "Receipt") */
  documentType?: string;
  /** Document date (ISO string) */
  documentDate?: string;
  /** Document description */
  description?: string;
  /** Linked objects for future use */
  linkedObjects?: LinkedObject[];
  /** Animation duration in seconds */
  animationDuration?: number;
  /** Whether the document is expanded */
  isExpanded?: boolean;
  /** Toggle expansion handler */
  onToggle?: () => void;
  /** Whether this is an even row (for alternating colors) */
  isEvenRow?: boolean;
  /** Document status */
  status?: 'completed' | 'processing' | 'error';
  /** Original filename (for processing/error states) */
  filename?: string;
  /** Note/message (current step or error message) */
  note?: string;
  /** Preview handler (opens fullscreen document viewer) */
  onPreview?: () => void;
  /** Delete handler */
  onDelete?: () => void;
  /** Delete button label (for i18n) */
  deleteLabel?: string;
  /** Preview button label (for i18n) */
  previewLabel?: string;
  /** Whether the document is waiting (queued, not actively processing) */
  isWaiting?: boolean;
  /** Taxonomy category (from INDEX.yaml) */
  category?: string;
  /** Taxonomy subcategory (from INDEX.yaml) */
  subcategory?: string;
  /** Extraction tier: 1=full, 2=recognition, 3=fallback */
  tier?: number;
  /** Icon name from feedDisplay.icon in document type YAML (lucide-react icon name) */
  icon?: string;
  /** Highlight bubbles for folded view (from highlighted_facts) */
  highlightBubbles?: HighlightBubble[];
  /** Whether selection mode is active (shows checkboxes) */
  isSelectable?: boolean;
  /** Whether this document is currently selected */
  isSelected?: boolean;
  /** Selection change handler */
  onSelectionChange?: (selected: boolean) => void;
  /** Whether this document has been soft-deleted */
  isDeleted?: boolean;
  /** Restore handler (for soft-deleted documents) */
  onRestore?: () => void;
  /** Restore button label (for i18n) */
  restoreLabel?: string;
  /** Tags enabled for this document */
  tags?: Array<{ id: string; name: string; color: TagColor }>;
  /** All workspace tag definitions (for tag panel) */
  allTags?: TagDefinition[];
  /** Toggle a tag on/off for this document */
  onToggleTag?: (tagId: string) => void;
  /** Create a new tag in workspace */
  onCreateTag?: (name: string, color: TagColor) => void;
  /** Edit an existing tag */
  onEditTag?: (tagId: string, name: string, color: TagColor) => void;
  /** Delete a tag from workspace */
  onDeleteTag?: (tagId: string) => void;
  /** Tag button tooltip label (for i18n) */
  tagLabel?: string;
  /** Tag panel labels (for i18n) */
  tagPanelLabels?: {
    placeholder?: string;
    done?: string;
    deleteTag?: string;
    cancel?: string;
  };
  /** Pages in the underlying document — drives the page-preview tile
   *  count in the expanded body (#755, #847). PDFs report their real
   *  page count; image-source docs (jpg/png/heic/tiff) report 1.
   *  ``null``/``undefined`` skips tiles entirely (unsupported file type,
   *  or metadata not yet resolved). */
  pageCount?: number | null;
  /** URL builder that returns the JPEG thumbnail URL for ``page`` of
   *  this document. Called once per rendered tile. When omitted, the
   *  tiles render their skeleton state (used in stories / pre-wired
   *  consumers). */
  pageThumbnailUrl?: (page: number) => string;
  /** Click handler fired by a page-preview tile with its 1-indexed
   *  page number. Wired in the consumer to open the preview modal at
   *  the matching page (``usePreviewState.handlePreview(docId, fn,
   *  page)``). */
  onPreviewPage?: (page: number) => void;
}

export const Document: React.FC<DocumentProps> = ({
  documentType = 'Invoice',
  documentDate = '',
  description,
  linkedObjects = [],
  animationDuration = 0.3,
  isExpanded = false,
  onToggle = () => {},
  isEvenRow = false,
  status = 'completed',
  filename,
  note,
  onPreview,
  onDelete: _onDelete,
  deleteLabel: _deleteLabel = 'Delete',
  previewLabel = 'Preview',
  isWaiting = false,
  category,
  subcategory: _subcategory,
  tier: _tier,
  icon,
  highlightBubbles = [],
  isSelectable = false,
  isSelected = false,
  onSelectionChange,
  isDeleted = false,
  onRestore,
  restoreLabel = 'Restore',
  tags = [],
  allTags = [],
  onToggleTag,
  onCreateTag,
  onEditTag,
  onDeleteTag,
  tagLabel = 'Tags',
  tagPanelLabels,
  pageCount,
  pageThumbnailUrl,
  onPreviewPage,
}) => {
  // These are passed for future use (delete handled at page level now)
  void _tier;
  void _subcategory;
  void _onDelete;
  void _deleteLabel;

  const contentRef = useRef<HTMLDivElement | null>(null);

  const handlePreviewClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onPreview?.();
  }, [onPreview]);

  const handleRestoreClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onRestore?.();
  }, [onRestore]);

  const handleSelectionChange = useCallback((checked: boolean) => {
    onSelectionChange?.(checked);
  }, [onSelectionChange]);

  // Determine icon for document type - use icon prop from YAML or fallback to FileText
  const docIcon = getIconByName(icon);

  // Helper to format the date nicely
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };
    return new Date(dateStr).toLocaleDateString('en-US', options);
  };

  // Render processing state
  if (status === 'processing') {
    return (
      <div className={`document-row ${isEvenRow ? 'document-row-even' : 'document-row-odd'}`}>
        <div className="document-header document-header-static">
          <div className="document-header-left">
            <div className="document-icon">
              {isWaiting ? (
                <Clock className="document-icon-svg document-icon-waiting" />
              ) : (
                <div className="document-spinner" />
              )}
            </div>
            <div className="document-bubble document-bubble-filename">
              {filename || 'document.pdf'}
            </div>
            {note && <span className="document-note">{note}</span>}
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (status === 'error') {
    return (
      <div className={`document-row ${isEvenRow ? 'document-row-even' : 'document-row-odd'}${isSelected ? ' document-row-selected' : ''}`}>
        <div className="document-header document-header-static">
          <div className="document-header-left">
            {isSelectable && (
              <Checkbox
                checked={isSelected}
                onChange={handleSelectionChange}
                ariaLabel={isSelected ? 'Deselect document' : 'Select document'}
                className="document-selection-checkbox"
              />
            )}
            <div className="document-icon document-icon-error">
              <AlertCircle className="document-icon-svg" />
            </div>
            <div className="document-bubble document-bubble-filename">
              {filename || 'document.pdf'}
            </div>
            {note && <span className="document-note document-note-error">{note}</span>}
          </div>
        </div>
      </div>
    );
  }

  // Render completed state (expandable)
  return (
    <div className={`document-row ${isExpanded ? 'document-row-expanded' : (isEvenRow ? 'document-row-even' : 'document-row-odd')}${isSelected ? ' document-row-selected' : ''}${isDeleted ? ' document-row-deleted' : ''}`}>
      {/* Document Header - Always Visible */}
      <div
        className={`document-header ${isExpanded ? 'document-header-expanded' : ''}`}
        onClick={onToggle}
      >
        <div className="document-header-left">
          {/* Selection checkbox - shown only in selection mode */}
          {isSelectable && (
            <Checkbox
              checked={isSelected}
              onChange={handleSelectionChange}
              ariaLabel={isSelected ? 'Deselect document' : 'Select document'}
              className="document-selection-checkbox"
            />
          )}
          <div className="document-icon-wrapper">
            <button
              className="document-icon-button"
              onClick={handlePreviewClick}
              aria-label={previewLabel}
              title={previewLabel}
            >
              {docIcon}
            </button>
            {isDeleted && (
              <div className="document-deleted-badge">
                <Trash2 className="document-deleted-badge-icon" />
              </div>
            )}
          </div>

          {/* Document type and category - stacked layout */}
          <div className="document-type-stack">
            <span className="document-type-label">{documentType}</span>
            {category && <span className="document-category-label">{category}</span>}
          </div>

          {/* Highlight bubbles (from highlighted_facts) */}
          {highlightBubbles.length > 0 && (
            <div className="document-highlight-bubbles">
              {highlightBubbles.map((bubble, index) => {
                // Infer format from field name if not explicitly specified (backward compat)
                const inferredFormat = bubble.format || (
                  bubble.name === 'sender' ? 'sender_name' :
                  bubble.name === 'email_date' ? 'datetime_de' :
                  undefined
                );
                const formattedValue = formatValue(bubble.value, inferredFormat);
                return (
                  <div
                    key={`${bubble.name}-${index}`}
                    className={`document-bubble document-bubble-highlight document-bubble-highlight-${bubble.style}`}
                  >
                    {bubble.displayType === 'value-only'
                      ? <>{formattedValue}</>
                      : <>
                          {bubble.label}
                          {bubble.displayType === 'scale'
                            ? <> {renderScaleValue(bubble.value, bubble.scaleType)}</>
                            : bubble.displayType === 'composite'
                            ? <> {formattedValue}</>
                            : <>: {formattedValue}</>
                          }
                        </>
                    }
                  </div>
                );
              })}
            </div>
          )}

          {/* Tag chips (enabled tags for this document) */}
          {tags.length > 0 && (
            <div className="document-tag-chips">
              {tags.map((tag) => (
                <TagChip key={tag.id} name={tag.name} color={tag.color} />
              ))}
            </div>
          )}

          {/* Tag panel trigger + popover */}
          {onToggleTag && (
            <TagPanel
              tags={allTags}
              enabledTagIds={tags.map((t) => t.id)}
              onToggleTag={onToggleTag}
              onCreateTag={onCreateTag || (() => {})}
              onEditTag={onEditTag || (() => {})}
              onDeleteTag={onDeleteTag || (() => {})}
              triggerLabel={tagLabel}
              labels={tagPanelLabels}
            />
          )}

        </div>

        {/* Restore button - shown only for deleted documents */}
        {isDeleted && (
          <button
            className="document-restore-button"
            onClick={handleRestoreClick}
            aria-label={restoreLabel}
            title={restoreLabel}
          >
            <Undo2 className="document-restore-icon" />
          </button>
        )}
      </div>

      {/* Expandable Content */}
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{
          height: isExpanded ? 'auto' : 0,
          opacity: isExpanded ? 1 : 0,
        }}
        transition={{ duration: animationDuration }}
        className="document-content-wrapper"
      >
        <div ref={contentRef} className="document-content">
          {/* Document date */}
          {documentDate && (
            <div className="document-meta">
              <span className="document-date">{formatDate(documentDate)}</span>
            </div>
          )}

          {/* Two-column body (#836): description (left) + page-preview
              tiles (right) on wide viewports; stacks on narrow viewports
              via .document-content-body--two-column CSS. The wrapper is
              only emitted when page tiles will render so single-column
              layouts (no PDF / metadata not resolved) stay unchanged.
              #1574: the `--has-description` modifier is set only when a
              description actually fills the left column. The tile column's
              -2rem top-margin (which pulls the tiles up to align with the
              date row) is gated behind that modifier, so a descriptionless
              card — where the tiles are the lone child — keeps the tiles
              below .document-meta and the date stays visible. */}
          {pageCount != null && pageCount >= 1 && pageThumbnailUrl ? (
            <div
              className={`document-content-body document-content-body--two-column${
                description ? ' document-content-body--has-description' : ''
              }`}
            >
              {description && (
                <div className="document-description-wrapper">
                  <p className="document-description">{description}</p>
                </div>
              )}

              {/* Page-preview tiles (#755) — renders 1 or 2 thumbnails plus
                  a "+N more" tile when the doc has additional pages. */}
              <div className="document-page-tiles">
                <PagePreview
                  page={1}
                  src={pageThumbnailUrl(1)}
                  onClick={onPreviewPage}
                />
                {pageCount >= 2 && (
                  <PagePreview
                    page={2}
                    src={pageThumbnailUrl(2)}
                    onClick={onPreviewPage}
                  />
                )}
                {pageCount > 2 && (
                  <PagePreview
                    page={3}
                    src={null}
                    overflowCount={pageCount - 2}
                    onClick={onPreviewPage}
                  />
                )}
              </div>
            </div>
          ) : (
            /* Single-column fallback: no page tiles to render, so
               description (if any) stays in the column flow at full
               width as it did before #836. */
            description && (
              <div className="document-description-wrapper">
                <p className="document-description">{description}</p>
              </div>
            )
          )}

          {/* Linked objects */}
          {linkedObjects.length > 0 && (
            <div className="document-linked-objects">
              {linkedObjects.map((obj) => (
                <div key={obj.id} className="document-bubble document-bubble-linked">
                  <LinkIcon className="document-bubble-icon" strokeWidth={2.5} />
                  {obj.name}
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Document;
